/**
 * Config-driven resources of the `marketing` admin-module group (marketing lists / subscribers / surveys / WhatsApp,
 * Royal Club, education). One generic list screen + one generic detail screen render all of them, so every admin
 * page has a native counterpart without near-duplicate screens. Pure data + helpers (no React).
 *
 * Resource shape:
 *   titleKey   i18n key of the screen / tab title
 *   icon, tone ManagementUI glyph + tone
 *   idKeys     candidate id fields (default `_id`, `id`)
 *   list       {path(params), keys[], search?, searchParam?, filters?[{param, labelKey, values[]}], paged?, pageSize?,
 *              query?, extract?(res), filter?(item), headerActions?[{id, icon, method, path}]}
 *   row        {title[], subtitle[], meta[], badge, icon}
 *   open       {screen, params(item, id)} - open another native screen instead of the generic detail
 *   detail     {path(id, item), keys[], statsPath?(id), settingsSpec?, related?[{resource, icon}]}
 *   actions    [{id, icon, tone, method, path(item,id), body?(item,input), when?(item), input?, closes?}]
 *   create     {path, fields[]}    edit {path(item,id), method, fields[]}   (fields: {key, type, required, options})
 *   singleton  {path, keys[]}      a document screen without list (edit applies to it directly)
 */

const enc = (value) => encodeURIComponent(String(value));

/** Keys never shown in the generic detail. */
export const HIDDEN_KEYS = ['__v', '_id', 'id', 'restaurant', 'restaurantId', 'passwordHash', 'password', 'token', 'secret'];

/**
 * Human readable fallback label (`maxSeatsPerBooking` -> `Max seats per booking`).
 * @param {string} name Field name.
 * @returns {string} Label.
 */
export const humanize = (name) => {
    const spaced = String(name || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_.-]+/g, ' ').trim();
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase() : '';
};

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Text of a `{hu, en, ...}` localized object (or the string itself).
 * @param {*} value Raw value.
 * @param {string} [lang] UI language.
 * @returns {string} Text or ''.
 */
export const pickLocalized = (value, lang = 'hu') => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (!isPlainObject(value)) {
        return '';
    }
    const base = String(lang || 'hu').toLowerCase().split('-')[0];
    const candidates = [value[base], value.hu, value.en].concat(Object.values(value));
    const hit = candidates.find((entry) => typeof entry === 'string' && entry.trim());
    return hit || '';
};

/**
 * Reads a dotted path.
 * @param {object} obj Source.
 * @param {string} path `a.b.c`.
 * @returns {*} Value or undefined.
 */
export const getPath = (obj, path) => String(path).split('.').reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), obj);

/**
 * First non-empty text among the candidate keys.
 * @param {object} item Record.
 * @param {string[]} keys Candidate keys (dotted ok).
 * @param {string} lang UI language.
 * @returns {string} Text or ''.
 */
export const firstText = (item, keys, lang) => {
    for (let i = 0; i < (keys || []).length; i += 1) {
        const text = pickLocalized(getPath(item, keys[i]), lang);
        if (text) {
            return text;
        }
    }
    return '';
};

/**
 * Record id.
 * @param {object} item Record.
 * @param {string[]} [idKeys] Candidate id fields.
 * @returns {string} Id or ''.
 */
export const idOf = (item, idKeys = ['_id', 'id']) => {
    for (let i = 0; i < idKeys.length; i += 1) {
        const value = item?.[idKeys[i]];
        if (value !== undefined && value !== null && value !== '') {
            return String(value);
        }
    }
    return '';
};

/**
 * Rows of a list response: a bare array, a configured key, `data`, or the first array property.
 * @param {*} res API response.
 * @param {string[]} [keys] Preferred keys.
 * @returns {object[]} Rows.
 */
export const extractRows = (res, keys = []) => {
    if (Array.isArray(res)) {
        return res;
    }
    if (!isPlainObject(res)) {
        return [];
    }
    for (let i = 0; i < keys.length; i += 1) {
        if (Array.isArray(res[keys[i]])) {
            return res[keys[i]];
        }
    }
    if (Array.isArray(res.data)) {
        return res.data;
    }
    if (isPlainObject(res.data)) {
        return extractRows(res.data, keys);
    }
    const hit = Object.values(res).find((value) => Array.isArray(value));
    return hit || [];
};

/**
 * Whether more pages exist.
 * @param {*} res API response.
 * @param {number} page Current page.
 * @param {number} loaded Rows loaded so far (incl. this page).
 * @param {number} rowsLen Rows of this page.
 * @param {number} pageSize Page size.
 * @returns {boolean} True when another page can be requested.
 */
export const hasMorePages = (res, page, loaded, rowsLen, pageSize) => {
    const pag = (isPlainObject(res) && (res.pagination || res.meta)) || res || {};
    const pages = Number(pag.pages || pag.totalPages);
    if (pages) {
        return page < pages;
    }
    const total = Number(pag.total || pag.totalCount);
    if (total) {
        return loaded < total;
    }
    return rowsLen >= pageSize;
};

/**
 * Detail record of a response.
 * @param {*} res API response.
 * @param {string[]} [keys] Preferred wrapper keys.
 * @returns {object|null} Record.
 */
export const unwrapDetail = (res, keys = []) => {
    if (!isPlainObject(res)) {
        return null;
    }
    for (let i = 0; i < keys.length; i += 1) {
        if (isPlainObject(res[keys[i]])) {
            return res[keys[i]];
        }
    }
    if (isPlainObject(res.data)) {
        return unwrapDetail(res.data, keys);
    }
    return res;
};

const isLocalizedObject = (value) => isPlainObject(value) && ['hu', 'en', 'de', 'ro'].some((k) => typeof value[k] === 'string');

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Display text of a scalar / localized value.
 * @param {*} value Raw value.
 * @param {Function} t i18n.
 * @param {string} lang UI language.
 * @returns {string} Text ('' when not displayable).
 */
export const formatValue = (value, t, lang) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    if (typeof value === 'boolean') {
        return t(value ? 'adminmod_marketing_g_yes' : 'adminmod_marketing_g_no');
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value.toLocaleString() : '';
    }
    if (typeof value === 'string') {
        if (ISO_DATE.test(value)) {
            const date = new Date(value);
            return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
        }
        return value;
    }
    if (Array.isArray(value)) {
        const parts = value.map((entry) => (isPlainObject(entry) ? (isLocalizedObject(entry) ? pickLocalized(entry, lang) : '') : formatValue(entry, t, lang))).filter(Boolean);
        return parts.length ? parts.join(', ') : '';
    }
    return isLocalizedObject(value) ? pickLocalized(value, lang) : '';
};

/**
 * Display fields of a record: scalars, localized objects, scalar arrays and one level of nested objects.
 * @param {object} item Record.
 * @param {Function} t i18n.
 * @param {string} lang UI language.
 * @returns {{key: string, label: string, value: string}[]} Fields.
 */
export const autoFields = (item, t, lang) => {
    const out = [];
    if (!isPlainObject(item)) {
        return out;
    }
    const label = (key) => {
        const last = String(key).split('.').pop();
        return t(`adminmod_marketing_f_${last}`, humanize(last));
    };
    Object.keys(item).forEach((key) => {
        if (HIDDEN_KEYS.includes(key)) {
            return;
        }
        const value = item[key];
        const text = formatValue(value, t, lang);
        if (text) {
            out.push({key, label: label(key), value: text});
            return;
        }
        if (isPlainObject(value)) {
            Object.keys(value).forEach((sub) => {
                if (HIDDEN_KEYS.includes(sub)) {
                    return;
                }
                const subText = formatValue(value[sub], t, lang);
                if (subText && !isPlainObject(value[sub])) {
                    out.push({key: `${key}.${sub}`, label: label(sub), value: subText});
                }
            });
        }
    });
    return out;
};

const reasonOptions = ['manual', 'unsubscribe', 'bounce', 'complaint'];
const paymentOptions = ['cash', 'card', 'transfer', 'other'];

/** @type {Object<string, object>} */
export const RESOURCES = {
    lists: {
        titleKey: 'adminmod_marketing_page_listak',
        icon: 'format-list-bulleted',
        tone: 'violet',
        list: {path: () => '/marketing/lists', keys: ['lists'], search: true, paged: true, pageSize: 25},
        row: {title: ['name'], subtitle: ['description'], meta: ['subscriberCount', 'count', 'createdAt'], icon: 'format-list-bulleted'},
        detail: {path: (id) => `/marketing/lists/${enc(id)}`, keys: ['list'], related: [{resource: 'subscribers', icon: 'account-multiple-plus-outline', param: 'listId'}]},
        actions: [
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_i, id) => `/marketing/lists/${enc(id)}/duplicate`, closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/marketing/lists/${enc(id)}`, closes: true},
        ],
        create: {path: '/marketing/lists', fields: [{key: 'name', required: true}, {key: 'description', type: 'multiline'}]},
        edit: {path: (_i, id) => `/marketing/lists/${enc(id)}`, method: 'PUT', fields: [{key: 'name', required: true}, {key: 'description', type: 'multiline'}]},
    },
    subscribers: {
        titleKey: 'adminmod_marketing_page_feliratkozok',
        icon: 'account-multiple-plus-outline',
        tone: 'info',
        list: {
            path: () => '/marketing/subscribers',
            keys: ['subscribers'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['subscribed', 'unsubscribed']}],
        },
        row: {title: ['name', 'email'], subtitle: ['email'], meta: ['source', 'createdAt'], badge: 'status', icon: 'account-outline'},
        detail: {path: null},
        actions: [
            {id: 'unsubscribe', icon: 'account-cancel-outline', method: 'PUT', path: (_i, id) => `/marketing/subscribers/${enc(id)}`, body: () => ({status: 'unsubscribed'}), when: (i) => i.status !== 'unsubscribed', closes: true},
            {id: 'resubscribe', icon: 'account-check-outline', method: 'PUT', path: (_i, id) => `/marketing/subscribers/${enc(id)}`, body: () => ({status: 'subscribed'}), when: (i) => i.status === 'unsubscribed', closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/marketing/subscribers/${enc(id)}`, closes: true},
        ],
        create: {
            path: '/marketing/subscribers',
            fields: [{key: 'email', required: true, keyboard: 'email-address'}, {key: 'name'}, {key: 'status', type: 'chips', options: ['subscribed', 'unsubscribed'], initial: 'subscribed'}],
        },
        edit: {path: (_i, id) => `/marketing/subscribers/${enc(id)}`, method: 'PUT', fields: [{key: 'name'}, {key: 'email', keyboard: 'email-address'}]},
    },
    suppressions: {
        titleKey: 'adminmod_marketing_page_leiratkozottak',
        icon: 'cancel',
        tone: 'danger',
        idKeys: ['email', '_id', 'id'],
        list: {path: () => '/email-suppressions', keys: ['suppressions', 'items'], search: true, paged: true, pageSize: 25},
        row: {title: ['email'], subtitle: ['detail'], meta: ['createdAt'], badge: 'reason', icon: 'email-off-outline'},
        detail: {path: null},
        actions: [{id: 'remove', icon: 'email-check-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/email-suppressions/${enc(id)}`, closes: true}],
        create: {
            path: '/email-suppressions',
            fields: [{key: 'email', required: true, keyboard: 'email-address'}, {key: 'reason', type: 'chips', options: reasonOptions, initial: 'manual'}, {key: 'detail'}],
        },
    },
    campaignStats: {
        titleKey: 'adminmod_marketing_page_marketing-statisztikak',
        icon: 'chart-timeline-variant',
        tone: 'info',
        list: {
            path: () => '/email-campaigns',
            keys: ['campaigns', 'items'],
            paged: false,
            query: {limit: 100},
            filter: (item) => !!item.status && !['draft', 'scheduled', 'canceled', 'cancelled'].includes(item.status),
        },
        row: {title: ['name', 'subject'], subtitle: ['subject'], meta: ['sentAt', 'updatedAt'], badge: 'status', icon: 'chart-box-outline'},
        open: {screen: 'MarketingCampaignDetailScreen', params: (_i, id) => ({id})},
    },
    emailTemplates: {
        titleKey: 'adminmod_marketing_page_sablonok',
        icon: 'palette-swatch-outline',
        tone: 'violet',
        list: {
            path: () => '/campaign-templates',
            keys: ['system', 'restaurant', 'templates'],
            paged: false,
            search: false,
            extract: (res) => {
                if (Array.isArray(res)) {
                    return res;
                }
                const data = isPlainObject(res?.data) ? res.data : res;
                const rows = [];
                ['restaurant', 'system', 'templates'].forEach((key) => {
                    if (Array.isArray(data?.[key])) {
                        data[key].forEach((entry) => rows.push(entry && entry.scope ? entry : {...entry, scope: key}));
                    }
                });
                return rows;
            },
        },
        row: {title: ['name', 'title'], subtitle: ['subject', 'category'], meta: ['updatedAt'], badge: 'scope', icon: 'palette-swatch-outline'},
        detail: {path: null},
        actions: [
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_i, id) => `/campaign-templates/${enc(id)}/duplicate`, closes: true},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/campaign-templates/${enc(id)}`, when: (i) => i.scope !== 'system', closes: true},
        ],
    },
    surveys: {
        titleKey: 'adminmod_marketing_page_kerdoivek',
        icon: 'poll',
        tone: 'primary',
        idKeys: ['_id', 'id', 'surveyId'],
        list: {
            path: () => '/marketing/surveys',
            keys: ['surveys'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['draft', 'published', 'closed']}],
        },
        row: {title: ['title', 'name'], subtitle: ['description'], meta: ['responseCount', 'createdAt'], badge: 'status', icon: 'poll'},
        detail: {
            path: (id) => `/marketing/surveys/${enc(id)}`,
            keys: ['survey'],
            statsPath: (id) => `/marketing/surveys/${enc(id)}/stats`,
            related: [{resource: 'surveyResponses', icon: 'comment-text-multiple-outline'}],
        },
        actions: [
            {id: 'publish', icon: 'publish', method: 'POST', path: (_i, id) => `/marketing/surveys/${enc(id)}/publish`, when: (i) => i.status === 'draft' || i.status === 'unpublished'},
            {id: 'unpublish', icon: 'eye-off-outline', method: 'POST', path: (_i, id) => `/marketing/surveys/${enc(id)}/unpublish`, when: (i) => i.status === 'published'},
            {id: 'close', icon: 'lock-outline', method: 'POST', path: (_i, id) => `/marketing/surveys/${enc(id)}/close`, when: (i) => i.status === 'published'},
            {id: 'reopen', icon: 'lock-open-outline', method: 'POST', path: (_i, id) => `/marketing/surveys/${enc(id)}/reopen`, when: (i) => i.status === 'closed'},
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_i, id) => `/marketing/surveys/${enc(id)}/duplicate`, closes: true},
            {id: 'archive', icon: 'archive-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/marketing/surveys/${enc(id)}`, closes: true},
        ],
    },
    surveyResponses: {
        titleKey: 'adminmod_marketing_r_surveyResponses',
        icon: 'comment-text-multiple-outline',
        tone: 'primary',
        list: {path: (p) => `/marketing/surveys/${enc(p.parentId)}/responses`, keys: ['responses'], paged: true, pageSize: 25},
        row: {title: ['respondent.email', 'email', 'respondent.name', 'name', 'createdAt'], subtitle: ['createdAt'], meta: [], icon: 'comment-text-outline'},
        detail: {path: null},
    },
    loyaltyPrograms: {
        titleKey: 'adminmod_marketing_page_loyalty-programs',
        icon: 'format-list-bulleted',
        tone: 'violet',
        list: {
            path: () => '/loyalty/programs',
            keys: ['programs'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['draft', 'active', 'paused', 'archived']}],
        },
        row: {title: ['name'], subtitle: ['type'], meta: ['version', 'updatedAt'], badge: 'status', icon: 'star-circle-outline'},
        detail: {path: (id) => `/loyalty/programs/${enc(id)}`, keys: ['program']},
        actions: [
            {id: 'publish', icon: 'publish', method: 'POST', path: (_i, id) => `/loyalty/programs/${enc(id)}/publish`, body: () => ({}), when: (i) => i.status === 'draft'},
            {id: 'pause', icon: 'pause', method: 'POST', path: (_i, id) => `/loyalty/programs/${enc(id)}/pause`, when: (i) => i.status === 'active'},
            {id: 'archive', icon: 'archive-outline', tone: 'danger', method: 'POST', path: (_i, id) => `/loyalty/programs/${enc(id)}/archive`, when: (i) => i.status !== 'archived'},
        ],
    },
    rewards: {
        titleKey: 'adminmod_marketing_page_loyalty-rewards',
        icon: 'gift-outline',
        tone: 'warning',
        list: {
            path: () => '/loyalty/rewards',
            keys: ['rewards'],
            search: true,
            paged: true,
            pageSize: 25,
            filters: [{param: 'active', labelKey: 'adminmod_marketing_f_active', values: ['true', 'false']}],
        },
        row: {title: ['name', 'title'], subtitle: ['type', 'description'], meta: ['inventory.remaining', 'cost'], badge: 'active', icon: 'gift-outline'},
        detail: {path: (id) => `/loyalty/rewards/${enc(id)}`, keys: ['reward']},
        actions: [
            {id: 'activate', icon: 'check-circle-outline', method: 'PUT', path: (_i, id) => `/loyalty/rewards/${enc(id)}`, body: () => ({active: true}), when: (i) => i.active === false},
            {id: 'deactivate', icon: 'pause-circle-outline', method: 'PUT', path: (_i, id) => `/loyalty/rewards/${enc(id)}`, body: () => ({active: false}), when: (i) => i.active !== false},
            {id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/loyalty/rewards/${enc(id)}`, closes: true},
        ],
    },
    privacy: {
        titleKey: 'adminmod_marketing_page_loyalty-privacy',
        icon: 'shield-account-outline',
        tone: 'danger',
        idKeys: ['id', '_id'],
        list: {
            path: () => '/data-subject-requests',
            keys: ['requests', 'items'],
            paged: true,
            pageSize: 25,
            filters: [
                {param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['open', 'in_progress', 'completed', 'rejected']},
                {param: 'type', labelKey: 'adminmod_marketing_f_type', values: ['export', 'delete', 'rectify']},
            ],
        },
        row: {title: ['guest.name', 'email', 'type'], subtitle: ['type', 'note'], meta: ['createdAt'], badge: 'status', icon: 'shield-account-outline'},
        detail: {path: null},
        actions: [
            {id: 'start', icon: 'play-circle-outline', method: 'POST', path: (_i, id) => `/data-subject-requests/${enc(id)}/process`, body: (_i, input) => ({action: 'start', note: input || undefined}), when: (i) => i.status === 'open', input: {key: 'note', optional: true}},
            {id: 'complete', icon: 'check-circle-outline', method: 'POST', path: (_i, id) => `/data-subject-requests/${enc(id)}/process`, body: (_i, input) => ({action: 'complete', note: input || undefined}), when: (i) => !['completed', 'rejected'].includes(i.status), input: {key: 'note', optional: true}},
            {id: 'reject', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: (_i, id) => `/data-subject-requests/${enc(id)}/process`, body: (_i, input) => ({action: 'reject', note: input || undefined}), when: (i) => !['completed', 'rejected'].includes(i.status), input: {key: 'note', optional: true}},
        ],
    },
    appModules: {
        titleKey: 'adminmod_marketing_page_loyalty-app-modules',
        icon: 'apps',
        tone: 'primary',
        idKeys: ['key', '_id'],
        list: {path: () => '/loyalty/guest-modules', keys: ['modules'], paged: false},
        row: {title: ['labels', 'key'], subtitle: ['description'], meta: [], badge: 'enabled', icon: 'puzzle-outline'},
        detail: {path: null, settingsSpec: true, settingsPath: (id) => `/loyalty/guest-modules/${enc(id)}`},
        actions: [
            {id: 'enable', icon: 'toggle-switch', method: 'PUT', path: (_i, id) => `/loyalty/guest-modules/${enc(id)}`, body: () => ({enabled: true}), when: (i) => i.enabled !== true},
            {id: 'disable', icon: 'toggle-switch-off-outline', tone: 'danger', method: 'PUT', path: (_i, id) => `/loyalty/guest-modules/${enc(id)}`, body: () => ({enabled: false}), when: (i) => i.enabled === true},
        ],
    },
    events: {
        titleKey: 'adminmod_marketing_page_loyalty-events',
        icon: 'calendar-star',
        tone: 'warning',
        list: {
            path: () => '/loyalty/events',
            keys: ['events'],
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['draft', 'published', 'cancelled', 'completed']}],
        },
        row: {title: ['title', 'name'], subtitle: ['location', 'description'], meta: ['startsAt', 'capacity'], badge: 'status', icon: 'calendar-star'},
        detail: {path: null, related: [{resource: 'eventBookings', icon: 'ticket-confirmation-outline'}]},
        actions: [{id: 'cancel', icon: 'calendar-remove-outline', tone: 'danger', method: 'POST', path: (_i, id) => `/loyalty/events/${enc(id)}/cancel`, body: () => ({}), when: (i) => !['cancelled', 'canceled', 'completed'].includes(i.status)}],
    },
    eventBookings: {
        titleKey: 'adminmod_marketing_r_eventBookings',
        icon: 'ticket-confirmation-outline',
        tone: 'warning',
        list: {path: (p) => `/loyalty/events/${enc(p.parentId)}/bookings`, keys: ['bookings'], paged: true, pageSize: 50},
        row: {title: ['guest.name', 'name', 'guest.email', 'memberCode'], subtitle: ['guest.email', 'seats'], meta: ['createdAt'], badge: 'status', icon: 'ticket-outline'},
        detail: {path: null},
    },
    offers: {
        titleKey: 'adminmod_marketing_page_loyalty-offers',
        icon: 'tag-outline',
        tone: 'success',
        list: {
            path: () => '/loyalty/offers',
            keys: ['offers'],
            paged: true,
            pageSize: 25,
            filters: [
                {param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['draft', 'active', 'expired', 'archived']},
                {param: 'placement', labelKey: 'adminmod_marketing_f_placement', values: ['home', 'menu', 'checkout']},
            ],
        },
        row: {title: ['title', 'name'], subtitle: ['description', 'placement'], meta: ['startsAt', 'endsAt'], badge: 'status', icon: 'tag-outline'},
        detail: {path: null},
        actions: [{id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/loyalty/offers/${enc(id)}`, closes: true}],
    },
    membershipPlans: {
        titleKey: 'adminmod_marketing_r_membershipPlans',
        icon: 'card-account-details-outline',
        tone: 'violet',
        list: {path: () => '/loyalty/membership/plans', keys: ['plans'], paged: true, pageSize: 25},
        row: {title: ['name', 'title'], subtitle: ['description'], meta: ['price', 'periodDays', 'period'], badge: 'status', icon: 'card-account-details-outline'},
        detail: {path: null},
        actions: [{id: 'archive', icon: 'archive-outline', tone: 'danger', method: 'POST', path: (_i, id) => `/loyalty/membership/plans/${enc(id)}/archive`, body: () => ({}), when: (i) => i.status !== 'archived', closes: true}],
    },
    membershipSubs: {
        titleKey: 'adminmod_marketing_r_membershipSubs',
        icon: 'account-credit-card-outline',
        tone: 'violet',
        list: {
            path: () => '/loyalty/membership/subscriptions',
            keys: ['subscriptions'],
            search: true,
            searchParam: 'memberCode',
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['pending', 'active', 'expired', 'cancelled']}],
        },
        row: {title: ['guest.name', 'memberCode', 'reference'], subtitle: ['planName', 'plan.name', 'reference'], meta: ['endsAt'], badge: 'status', icon: 'account-credit-card-outline'},
        detail: {path: null},
        actions: [
            {id: 'activate', icon: 'check-circle-outline', method: 'POST', path: (_i, id) => `/loyalty/membership/subscriptions/${enc(id)}/activate`, body: (_i, input) => ({paymentMethod: input || 'cash'}), when: (i) => i.status === 'pending', input: {key: 'paymentMethod', type: 'chips', options: paymentOptions, initial: 'cash'}},
            {id: 'extend', icon: 'calendar-plus', method: 'POST', path: (_i, id) => `/loyalty/membership/subscriptions/${enc(id)}/extend`, body: (i, input) => ({expectedEndsAt: i.endsAt, periods: 1, paymentMethod: input || 'cash'}), when: (i) => i.status === 'active', input: {key: 'paymentMethod', type: 'chips', options: paymentOptions, initial: 'cash'}},
            {id: 'cancelSub', icon: 'close-circle-outline', tone: 'danger', method: 'POST', path: (_i, id) => `/loyalty/membership/subscriptions/${enc(id)}/cancel`, body: (_i, input) => ({reason: input, revokeUnusedRewards: true}), when: (i) => ['pending', 'active'].includes(i.status), input: {key: 'reason', required: true}},
        ],
    },
    waConfig: {
        titleKey: 'adminmod_marketing_r_waConfig',
        icon: 'whatsapp',
        tone: 'success',
        singleton: {path: '/whatsapp/config', keys: ['config']},
        edit: {
            path: () => '/whatsapp/config',
            method: 'PUT',
            fields: [
                {key: 'defaultTransport', type: 'chips', options: ['official', 'proxy']},
                {key: 'phoneNumberId', secret: true},
                {key: 'wabaId', secret: true},
                {key: 'accessToken', secret: true, secure: true},
                {key: 'otpEnabled', type: 'switch'},
                {key: 'otpTemplateName'},
                {key: 'otpTemplateLang'},
            ],
        },
    },
    waTemplates: {
        titleKey: 'adminmod_marketing_r_waTemplates',
        icon: 'file-document-outline',
        tone: 'success',
        idKeys: ['name', '_id', 'id'],
        list: {
            path: () => '/whatsapp/templates',
            keys: ['templates'],
            paged: false,
            headerActions: [{id: 'sync', icon: 'sync', method: 'POST', path: '/whatsapp/templates/sync'}],
        },
        row: {title: ['name'], subtitle: ['category', 'language'], meta: ['updatedAt'], badge: 'status', icon: 'file-document-outline'},
        detail: {path: null},
        actions: [{id: 'delete', icon: 'trash-can-outline', tone: 'danger', method: 'DELETE', path: (_i, id) => `/whatsapp/templates/${enc(id)}`, closes: true}],
    },
    waMessages: {
        titleKey: 'adminmod_marketing_r_waMessages',
        icon: 'message-text-outline',
        tone: 'success',
        list: {path: () => '/whatsapp/messages', keys: ['messages'], paged: false, query: {limit: 100}},
        row: {title: ['to', 'from', 'phone'], subtitle: ['body', 'text', 'templateName'], meta: ['createdAt'], badge: 'status', icon: 'message-text-outline'},
        detail: {path: null},
    },
    courses: {
        titleKey: 'adminmod_marketing_page_oktatas-tanfolyamok',
        icon: 'book-open-page-variant-outline',
        tone: 'info',
        list: {
            path: () => '/education/courses',
            keys: ['courses', 'items'],
            search: true,
            searchParam: 'q',
            paged: true,
            pageSize: 25,
            filters: [{param: 'status', labelKey: 'adminmod_marketing_f_status', values: ['draft', 'published', 'registration', 'closed', 'in_progress', 'completed', 'cancelled', 'postponed']}],
        },
        row: {title: ['title', 'name'], subtitle: ['type', 'location'], meta: ['startDate', 'startsAt', 'capacity'], badge: 'status', icon: 'school-outline'},
        detail: {
            path: (id) => `/education/courses/${enc(id)}`,
            keys: ['course'],
            statsPath: (id) => `/education/courses/${enc(id)}/stats`,
            related: [
                {resource: 'courseRegistrations', icon: 'account-multiple-outline'},
                {resource: 'courseSessions', icon: 'calendar-clock-outline'},
            ],
        },
        actions: [
            {id: 'registration', icon: 'account-plus-outline', method: 'PATCH', path: (_i, id) => `/education/courses/${enc(id)}/status`, body: () => ({status: 'registration'}), when: (i) => ['draft', 'published'].includes(i.status)},
            {id: 'closed', icon: 'lock-outline', method: 'PATCH', path: (_i, id) => `/education/courses/${enc(id)}/status`, body: () => ({status: 'closed'}), when: (i) => i.status === 'registration'},
            {id: 'in_progress', icon: 'play-circle-outline', method: 'PATCH', path: (_i, id) => `/education/courses/${enc(id)}/status`, body: () => ({status: 'in_progress'}), when: (i) => i.status === 'closed'},
            {id: 'completed', icon: 'check-circle-outline', method: 'PATCH', path: (_i, id) => `/education/courses/${enc(id)}/status`, body: () => ({status: 'completed'}), when: (i) => i.status === 'in_progress'},
            {id: 'cancelled', icon: 'close-circle-outline', tone: 'danger', method: 'PATCH', path: (_i, id) => `/education/courses/${enc(id)}/status`, body: (_i, input) => ({status: 'cancelled', reason: input}), when: (i) => !['completed', 'cancelled'].includes(i.status), input: {key: 'reason', required: true}},
            {id: 'duplicate', icon: 'content-copy', method: 'POST', path: (_i, id) => `/education/courses/${enc(id)}/duplicate`, body: () => ({}), closes: true},
        ],
    },
    courseRegistrations: {
        titleKey: 'adminmod_marketing_r_courseRegistrations',
        icon: 'account-multiple-outline',
        tone: 'info',
        list: {path: (p) => `/education/courses/${enc(p.parentId)}/registrations`, keys: ['registrations', 'items'], paged: true, pageSize: 25},
        row: {title: ['name', 'participant.name', 'email'], subtitle: ['email', 'participant.email', 'company'], meta: ['createdAt'], badge: 'status', icon: 'account-outline'},
        detail: {path: null},
    },
    courseSessions: {
        titleKey: 'adminmod_marketing_r_courseSessions',
        icon: 'calendar-clock-outline',
        tone: 'info',
        list: {path: (p) => `/education/courses/${enc(p.parentId)}/sessions`, keys: ['sessions', 'items'], paged: false},
        row: {title: ['title', 'name', 'date', 'startsAt'], subtitle: ['location', 'instructorName'], meta: ['startsAt', 'endsAt'], badge: 'status', icon: 'calendar-clock-outline'},
        detail: {path: null},
    },
    eduReportsDashboard: {
        titleKey: 'adminmod_marketing_r_eduReportsDashboard',
        icon: 'chart-line',
        tone: 'info',
        singleton: {path: '/education/reports/dashboard', keys: ['dashboard', 'summary', 'totals']},
    },
    eduReportCourses: {
        titleKey: 'adminmod_marketing_r_eduReportCourses',
        icon: 'book-open-page-variant-outline',
        tone: 'info',
        list: {path: () => '/education/reports/courses', keys: ['courses', 'rows', 'items'], paged: false},
        row: {title: ['title', 'name', 'courseTitle'], subtitle: ['status'], meta: ['registrations', 'revenue'], icon: 'book-open-page-variant-outline'},
        detail: {path: null},
    },
    eduReportInstructors: {
        titleKey: 'adminmod_marketing_r_eduReportInstructors',
        icon: 'account-tie-outline',
        tone: 'info',
        list: {path: () => '/education/reports/instructors', keys: ['instructors', 'rows', 'items'], paged: false},
        row: {title: ['name', 'instructorName', 'instructor.name'], subtitle: ['email'], meta: ['courses', 'sessions'], icon: 'account-tie-outline'},
        detail: {path: null},
    },
    eduReportTrend: {
        titleKey: 'adminmod_marketing_r_eduReportTrend',
        icon: 'chart-timeline-variant',
        tone: 'info',
        list: {path: () => '/education/reports/registrations', keys: ['trend', 'months', 'rows', 'items'], paged: false},
        row: {title: ['month', 'label', 'period', 'date'], subtitle: [], meta: ['count', 'registrations'], icon: 'chart-timeline-variant'},
        detail: {path: null},
    },
    eduCertificates: {
        titleKey: 'adminmod_marketing_r_eduCertificates',
        icon: 'certificate-outline',
        tone: 'warning',
        list: {path: () => '/education/certificates', keys: ['certificates', 'items'], paged: true, pageSize: 25},
        row: {title: ['participantName', 'name', 'number', 'certificateNumber'], subtitle: ['courseTitle', 'course.title'], meta: ['issuedAt'], badge: 'status', icon: 'certificate-outline'},
        detail: {path: null},
    },
};
